import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout, Menu, Badge } from "antd";
import { useTranslation } from "react-i18next";
import Logo from "../../shared/assets/logistics.png";
import usePermissions from "../../hooks/usePermissions";
import { fetchSystemConfig, readCachedSystemConfig } from "../../shared/lib/systemConfig";
import {
  AppstoreOutlined,
  UserOutlined,
  GiftOutlined,
  TagsOutlined,
  ApartmentOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  BarChartOutlined,
  PieChartOutlined,
  DollarOutlined,
  LineChartOutlined,
  StockOutlined,
  OrderedListOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SwapOutlined,
  ClearOutlined,
  StopOutlined,
  ContainerOutlined,
  ImportOutlined,
} from "@ant-design/icons";
import "../../shared/styles/Sidebar.css";

const { Sider } = Layout;

const Sidebar = ({ collapsed, onCollapse, cartCount }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    fetchSystemConfig()
      .then(setSystemConfig)
      .catch(() => null);
  }, []);

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === "/") return ["dashboard"];
    if (path.startsWith("/GetCustomer")) return ["customers"];
    if (path.startsWith("/GetProducts")) return ["products"];
    if (path.startsWith("/categories")) return ["categories"];
    if (path.startsWith("/GetUnit")) return ["units"];
    if (path.startsWith("/roles")) return ["roles"];
    if (path.startsWith("/users")) return ["users"];
    if (path.startsWith("/settings/system")) return ["system-settings"];
    if (path.startsWith("/settings/exchange-rates")) return ["exchange-rates"];
    if (path.startsWith("/settings/maintenance")) return ["maintenance"];
    if (path === "/TableOrder" || path.startsWith("/TableOrder")) return ["orders"];
    if (path.startsWith("/cancellations")) return ["cancellations"];
    if (path === "/HomeShop") return ["homeshop"];
    if (path === "/CartShop") return ["cartshop"];
    if (path === "/Stock") return ["stock"];
    if (path === "/Reports") return ["reports-item"];
    if (path === "/CategoryProductReport") return ["categoryProductReport"];
    if (path === "/currency-totals") return ["currency-totals"];
    if (path === "/reports/payables-receivables") return ["payables-receivables-report"];
    if (path === "/reports/buying") return ["reports-buying"];
    if (path === "/reports/selling") return ["reports-selling"];
    if (path.startsWith("/vendors")) return ["vendors"];
    if (path.startsWith("/purchase-orders")) return ["purchase-orders"];
    return [];
  };

  const menuItems = useMemo(() => {
    const pick = (item) => {
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
        return null;
      }
      const { requiredPermission, ...rest } = item;
      return rest;
    };

    const SectionLabel = ({ children }) => (
      <span className="sidebar-section-label">{children}</span>
    );

    const items = [];

    const dash = pick({
      key: "dashboard",
      icon: <AppstoreOutlined />,
      label: <Link to="/">{t("dashboard")}</Link>,
      requiredPermission: "dashboard.view",
    });
    if (dash) items.push(dash);

    const salesChildren = [
      pick({
        key: "orders",
        icon: <OrderedListOutlined />,
        label: <Link to="/TableOrder">{t("orders")}</Link>,
        requiredPermission: "orders.index",
      }),
      pick({
        key: "cancellations",
        icon: <StopOutlined />,
        label: <Link to="/cancellations">{t("cancellationsReport", "Cancellations")}</Link>,
        requiredPermission: "orders.cancellations",
      }),
      pick({
        key: "homeshop",
        icon: <ShopOutlined />,
        label: <Link to="/HomeShop">{t("homeShop")}</Link>,
        requiredPermission: "orders.add",
      }),
      pick({
        key: "cartshop",
        icon: (
          <Badge count={cartCount} size="small" offset={[4, 0]}>
            <ShoppingCartOutlined />
          </Badge>
        ),
        label: <Link to="/CartShop">{t("cartShop")}</Link>,
        requiredPermission: "orders.add",
      }),
    ].filter(Boolean);

    if (salesChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionSales")}</SectionLabel>,
        children: salesChildren,
      });
    }

    const contactsChildren = [
      pick({
        key: "customers",
        icon: <UserOutlined />,
        label: <Link to="/GetCustomer">{t("customers")}</Link>,
        requiredPermission: "customers.index",
      }),
      pick({
        key: "vendors",
        icon: <ImportOutlined />,
        label: <Link to="/vendors">{t("vendorsMenu")}</Link>,
        requiredPermission: "vendors.index",
      }),
    ].filter(Boolean);

    if (contactsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionContacts")}</SectionLabel>,
        children: contactsChildren,
      });
    }

    const buyingChildren = [
      pick({
        key: "purchase-orders",
        icon: <ContainerOutlined />,
        label: <Link to="/purchase-orders">{t("purchaseOrdersMenu")}</Link>,
        requiredPermission: "purchase_orders.index",
      }),
    ].filter(Boolean);

    if (buyingChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionBuying")}</SectionLabel>,
        children: buyingChildren,
      });
    }

    const catalogChildren = [
      pick({
        key: "products",
        icon: <GiftOutlined />,
        label: <Link to="/GetProducts">{t("products")}</Link>,
        requiredPermission: "products.index",
      }),
      pick({
        key: "categories",
        icon: <TagsOutlined />,
        label: <Link to="/categories">{t("categories")}</Link>,
        requiredPermission: "categories.index",
      }),
      pick({
        key: "units",
        icon: <ApartmentOutlined />,
        label: <Link to="/GetUnit">{t("units")}</Link>,
        requiredPermission: "units.index",
      }),
      pick({
        key: "stock",
        icon: <StockOutlined />,
        label: <Link to="/Stock">{t("stock")}</Link>,
        requiredPermission: "stock.view",
      }),
    ].filter(Boolean);

    if (catalogChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionCatalog")}</SectionLabel>,
        children: catalogChildren,
      });
    }

    const reportsChildren = [];
    if (hasPermission("reports.view")) {
      reportsChildren.push(
        {
          key: "reports-item",
          icon: <BarChartOutlined />,
          label: <Link to="/Reports">{t("reports")}</Link>,
        },
        {
          key: "categoryProductReport",
          icon: <PieChartOutlined />,
          label: (
            <Link to="/CategoryProductReport">{t("Category Product Report")}</Link>
          ),
        },
        {
          key: "currency-totals",
          icon: <DollarOutlined />,
          label: <Link to="/currency-totals">{t("currencyTotalsMenu")}</Link>,
        },
        {
          key: "reports-buying",
          icon: <LineChartOutlined />,
          label: <Link to="/reports/buying">{t("reportBuyingMenu")}</Link>,
        },
        {
          key: "reports-selling",
          icon: <LineChartOutlined />,
          label: <Link to="/reports/selling">{t("reportSellingMenu")}</Link>,
        }
      );
    }
    if (hasPermission("reports.payables_receivables") || hasPermission("reports.view")) {
      reportsChildren.push({
        key: "payables-receivables-report",
        icon: <BarChartOutlined />,
        label: (
          <Link to="/reports/payables-receivables">{t("payablesReceivablesMenu")}</Link>
        ),
      });
    }
    if (reportsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionReports")}</SectionLabel>,
        children: reportsChildren,
      });
    }

    const settingsChildren = [
      pick({
        key: "system-settings",
        icon: <SettingOutlined />,
        label: <Link to="/settings/system">{t("systemSettings")}</Link>,
        requiredPermission: "users.index",
      }),
      pick({
        key: "exchange-rates",
        icon: <SwapOutlined />,
        label: <Link to="/settings/exchange-rates">{t("exchangeRates")}</Link>,
        requiredPermission: "users.index",
      }),
      hasPermission("maintenance.reset") || hasPermission("users.index")
        ? {
            key: "maintenance",
            icon: <ClearOutlined />,
            label: <Link to="/settings/maintenance">{t("maintenanceMenu")}</Link>,
          }
        : null,
    ].filter(Boolean);

    if (settingsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionSettings")}</SectionLabel>,
        children: settingsChildren,
      });
    }

    const adminChildren = [
      pick({
        key: "roles",
        icon: <SafetyCertificateOutlined />,
        label: <Link to="/roles">{t("roles")}</Link>,
        requiredPermission: "users.index",
      }),
      pick({
        key: "users",
        icon: <TeamOutlined />,
        label: <Link to="/users">{t("users")}</Link>,
        requiredPermission: "users.index",
      }),
    ].filter(Boolean);

    if (adminChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarAdministration")}</SectionLabel>,
        children: adminChildren,
      });
    }

    return items;
  }, [t, cartCount, hasPermission]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      width={260}
      collapsedWidth={72}
      className="app-sidebar"
      theme="dark"
    >
      <div className="sidebar-brand">
        <Link to="/" className="sidebar-brand-link">
          <img
            src={systemConfig?.sidebar_logo_url || Logo}
            alt={systemConfig?.site_name || "Velan"}
            className="sidebar-logo"
          />
          {!collapsed && (
            <span className="sidebar-brand-text" title={systemConfig?.site_name || "Velan"}>
              {systemConfig?.site_name || "Velan"}
            </span>
          )}
        </Link>
      </div>
      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        items={menuItems}
        className="sidebar-menu"
      />
    </Sider>
  );
};

export default Sidebar;
